import { Button } from '@listening/frontend/components/radix/button';
import { AnimatePresence, motion } from 'framer-motion';
import { HalfMoon, SunLight } from 'iconoir-react';
import { useTheme } from './theme-provider';

type DisplayTheme = 'dark' | 'light';

const THEME_SEQUENCE: Array<DisplayTheme> = ['light', 'dark'];

const iconByTheme: Record<
  DisplayTheme,
  { Icon: typeof SunLight; ariaLabel: string }
> = {
  light: {
    Icon: SunLight,
    ariaLabel: 'Switch to dark theme',
  },
  dark: {
    Icon: HalfMoon,
    ariaLabel: 'Switch to light theme',
  },
};

const getNextTheme = (current: DisplayTheme): DisplayTheme => {
  const nextIndex =
    (THEME_SEQUENCE.indexOf(current) + 1) % THEME_SEQUENCE.length;
  return THEME_SEQUENCE[nextIndex] ?? 'light';
};

export function ThemeToggler() {
  const { theme, setThemePref } = useTheme();
  const { Icon, ariaLabel } = iconByTheme[theme];

  const button = (
    <Button
      onClick={() => {
        setThemePref(getNextTheme(theme));
      }}
      variant='ghost'
      size='icon'
      className='relative rounded-full text-navy-900 hover:text-navy-900 dark:text-white dark:hover:text-white'
      aria-label={ariaLabel}
    >
      <span className='sr-only'>{ariaLabel}</span>
      <AnimatePresence mode='wait' initial={false}>
        <motion.span
          key={theme}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.2 }}
          className='flex items-center justify-center'
        >
          <Icon className='size-[1.1rem] scale-120' />
        </motion.span>
      </AnimatePresence>
    </Button>
  );

  return button;
}
